import { Routes } from '@/api/routes';
import { apiClient } from '@/services/apiClient';
import type { PaymentMethod } from '@/types/checkout';
import { ProviderEnum } from '@/types/common';
import type { SafeChargeInstance, SafeChargeField, GetTokenResponse } from '@/types/nuvei';
import type { NuveiPaymentStatusResponse } from '@/types/payment';

export interface NuveiCardDetails {
  internalRequestId: number;
  status: string;
  errCode: number;
  merchantId: string;
  merchantSiteId: string;
  version: string;
  clientRequestId: string;
  sessionToken: string;
  brand: string;
  cardType: string;
  cardProduct: string;
  program: string;
  visaDirectSupport: string;
  issuerCountry: string;
  dccAllowed: boolean;
  isPrepaid: boolean;
  issuerBankName: string;
  mastercardSendSupport: string;
  isNotReloadableCard: boolean;
  bin: string;
  last4digits: string;
  ccExpMonth: string;
  ccExpYear: string;
}

interface BuildNuveiSaveCardPaymentRequestParams {
  email: string;
  paymentMethodId: string;
  sessionToken: string;
  cardholderName: string;
  cvc: string;
  pspIdentifier?: string;
}

interface BuildNuveiCardPaymentRequestParams {
  cardData: NuveiCardDetails;
  shouldSaveCard: boolean;
  paymentMethodId: string;
  cardholderName: string;
  email: string;
  sessionToken: string;
  pspIdentifier: string;
  transactionId?: string;
}

export const buildNuveiGenericPaymentPayload = ({
  paymentMethodSettings,
  params,
  sessionToken,
  email,
}: {
  paymentMethodSettings: PaymentMethod;
  params: Record<string, string>;
  sessionToken: string;
  email: string;
}) => {
  return {
    params: {
      sessionToken,
    },
    paymentProvider: 'nuvei',
    receipt_email: params.email ? params.email : email,
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
  };
};

export const buildNuveiSaveCardPaymentRequest = (params: BuildNuveiSaveCardPaymentRequestParams) => ({
  paymentProvider: ProviderEnum.NUVEI,
  receipt_email: params.email,
  params: {
    sessionToken: params.sessionToken,
    pspIdentifier: params.pspIdentifier,
    type: 'userPaymentOptions',
    cvv: params.cvc,
  },
  paymentMethodDisplayName: 'Credit Card',
  paymentMethodId: params.paymentMethodId,
});

/**
 * Get Nuvei card token
 */
export const getNuveiCardToken = async (
  safeChargeInstance: SafeChargeInstance,
  cardField: SafeChargeField,
  sessionToken: string,
  cardholderName: string,
  email: string,
  country: string
): Promise<GetTokenResponse | null> => {
  try {
    const response = (await safeChargeInstance.getToken(cardField, {
      sessionToken,
      cardHolderName: cardholderName,
      billingAddress: {
        email,
        country,
      },
    })) as GetTokenResponse;

    if (response.status === 'SUCCESS') {
      return response;
    }
    return null;
  } catch (error) {
    console.error('Error getting Nuvei card token:', error);
    return null;
  }
};

export const getNuveiCardDetails = async (
  safeChargeInstance: SafeChargeInstance,
  sessionToken: string
): Promise<NuveiCardDetails | null> => {
  try {
    const cardDetails = await new Promise<NuveiCardDetails>((resolve, reject) => {
      safeChargeInstance.getCardDetails(
        {
          sessionToken,
          useFields: true,
        },
        (response: unknown) => {
          if (response && typeof response === 'object') {
            resolve(response as NuveiCardDetails);
          } else {
            reject(new Error('Invalid card details response'));
          }
        }
      );
    });
    return cardDetails;
  } catch (error) {
    console.error('Error getting Nuvei card details:', error);
    return null;
  }
};

export const buildNuveiCardPaymentRequest = (params: BuildNuveiCardPaymentRequestParams) => ({
  paymentProvider: 'nuvei',
  receipt_email: params.email,
  paymentMethodDisplayName: 'Credit Card',
  paymentMethodId: params.paymentMethodId,
  params: {
    sessionToken: params.sessionToken,
    pspIdentifier: params.pspIdentifier,
    transactionId: params.transactionId,
    type: 'ccTempToken',
    shouldSaveCard: params.shouldSaveCard,
    cardDetails: {
      bin: params.cardData.bin,
      expirationMonth: params.cardData.ccExpMonth,
      expirationYear: params.cardData.ccExpYear,
      lastFourDigits: params.cardData.last4digits,
      nameOnCard: params.cardholderName ?? '',
      cardBrand: params.cardData.brand,
      cardType: '',
      countryOfIssuance: '',
    },
  },
});

export const getNuveiPaymentStatus = async (
  sessionToken: string,
  paymentMethodId: string
): Promise<NuveiPaymentStatusResponse | null> => {
  try {
    const response = await apiClient.get<NuveiPaymentStatusResponse>(
      `${Routes.PAYMENT_STATUS}?sessionToken=${sessionToken}&paymentMethodId=${paymentMethodId}`
    );
    return response;
  } catch (error) {
    console.error('Error getting Nuvei payment status:', error);
    return null;
  }
};
